import { OfferRequestBuildingType } from '@houzy/schemas/offer-request-management';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { Filters, type FilterSection } from '../../../components/filters/Filters';
import { inSet } from '../../../lib/typeGuards';
import {
    OFFER_REQUEST_BUILDING_TYPE_OPTIONS,
    OFFER_REQUEST_CREDITS_OPTIONS,
    OFFER_REQUEST_LANGUAGE_OPTIONS,
    OFFER_REQUEST_PROJECT_SIZE_OPTIONS,
    OFFER_REQUEST_STATUS_OPTIONS,
    OFFER_REQUEST_TIMING_GROUP_OPTIONS,
    formatCreditsLabel,
} from '../labels';
import { OfferRequestTimingGroup } from '../timing';
import {
    OfferRequestCredits,
    OfferRequestLanguage,
    OfferRequestProjectSize,
    OfferRequestStatus,
    type OfferRequestFiltersValue,
} from '../types';

export type OfferRequestsFiltersPanelProps = {
    value: OfferRequestFiltersValue;
    onChange: (value: OfferRequestFiltersValue) => void;
};

const SECTION_IDS = {
    dateRange: 'dateRange',
    timing: 'timing',
    size: 'size',
    credits: 'credits',
    language: 'language',
    buildingType: 'buildingType',
    status: 'status',
} as const;

type SectionId = (typeof SECTION_IDS)[keyof typeof SECTION_IDS];

// Single filter sidebar shared by the new- and open-request screens.
export function OfferRequestsFiltersPanel({ value, onChange }: OfferRequestsFiltersPanelProps): ReactElement {
    const { t } = useTranslation();

    const allowedTimingGroups = useMemo(
        () => new Set<OfferRequestTimingGroup>(Object.values(OfferRequestTimingGroup)),
        [],
    );
    const allowedSizes = useMemo(() => new Set<OfferRequestProjectSize>(Object.values(OfferRequestProjectSize)), []);
    const allowedCredits = useMemo(() => new Set<OfferRequestCredits>(Object.values(OfferRequestCredits)), []);
    const allowedLanguages = useMemo(() => new Set<OfferRequestLanguage>(Object.values(OfferRequestLanguage)), []);
    const allowedBuildingTypes = useMemo(
        () => new Set<OfferRequestBuildingType>(Object.values(OfferRequestBuildingType)),
        [],
    );
    const allowedStatuses = useMemo(() => new Set<OfferRequestStatus>(Object.values(OfferRequestStatus)), []);

    const sections = useMemo<FilterSection[]>(
        () => [
            {
                id: SECTION_IDS.dateRange,
                title: t('OFFER_REQUESTS.FILTERS.DATE_RANGE_SECTION'),
                content: (
                    <DateRangeFilter
                        startLabel={t('OFFER_REQUESTS.FILTERS.START_DATE')}
                        endLabel={t('OFFER_REQUESTS.FILTERS.END_DATE')}
                        clearLabel={t('OFFER_REQUESTS.FILTERS.CLEAR_DATES')}
                        value={{ from: value.dateFrom, to: value.dateTo }}
                        onChange={(range) => onChange({ ...value, dateFrom: range.from, dateTo: range.to })}
                    />
                ),
            },
            {
                id: SECTION_IDS.timing,
                title: t('OFFER_REQUESTS.FILTERS.URGENCY_SECTION'),
                options: OFFER_REQUEST_TIMING_GROUP_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                })),
            },
            {
                id: SECTION_IDS.size,
                title: t('OFFER_REQUESTS.FILTERS.SIZE_SECTION'),
                options: OFFER_REQUEST_PROJECT_SIZE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                })),
            },
            {
                id: SECTION_IDS.credits,
                title: t('OFFER_REQUESTS.FILTERS.CREDITS_SECTION'),
                options: OFFER_REQUEST_CREDITS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: formatCreditsLabel(t, o.credits),
                })),
            },
            {
                id: SECTION_IDS.language,
                title: t('OFFER_REQUESTS.FILTERS.LANGUAGE_SECTION'),
                options: OFFER_REQUEST_LANGUAGE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                })),
            },
            {
                id: SECTION_IDS.buildingType,
                title: t('OFFER_REQUESTS.FILTERS.BUILDING_TYPE_SECTION'),
                options: OFFER_REQUEST_BUILDING_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                })),
            },
            {
                id: SECTION_IDS.status,
                title: t('OFFER_REQUESTS.FILTERS.STATUS_SECTION'),
                options: OFFER_REQUEST_STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.labelKey),
                })),
            },
        ],
        [t, value, onChange],
    );

    const selected = useMemo<Record<string, string[]>>(
        () => ({
            [SECTION_IDS.timing]: value.timingGroups,
            [SECTION_IDS.size]: value.projectSizes,
            [SECTION_IDS.credits]: value.credits.map(String),
            [SECTION_IDS.language]: value.languages,
            [SECTION_IDS.buildingType]: value.buildingTypes,
            [SECTION_IDS.status]: value.statuses,
        }),
        [value.timingGroups, value.projectSizes, value.credits, value.languages, value.buildingTypes, value.statuses],
    );

    const handleSelectionChange = (sectionId: string, values: string[]): void => {
        const id = sectionId as SectionId;
        switch (id) {
            case SECTION_IDS.dateRange:
                // handled by the DateRangeFilter's own onChange, not selection state
                break;
            case SECTION_IDS.timing:
                onChange({
                    ...value,
                    timingGroups: values.filter(inSet(allowedTimingGroups)),
                });
                break;
            case SECTION_IDS.size:
                onChange({
                    ...value,
                    projectSizes: values.filter(inSet(allowedSizes)),
                });
                break;
            case SECTION_IDS.credits:
                onChange({
                    ...value,
                    credits: values.map(Number).filter(inSet(allowedCredits)),
                });
                break;
            case SECTION_IDS.language:
                onChange({
                    ...value,
                    languages: values.filter(inSet(allowedLanguages)),
                });
                break;
            case SECTION_IDS.buildingType:
                onChange({
                    ...value,
                    buildingTypes: values.filter(inSet(allowedBuildingTypes)),
                });
                break;
            case SECTION_IDS.status:
                onChange({ ...value, statuses: values.filter(inSet(allowedStatuses)) });
                break;
            default:
                id satisfies never;
        }
    };

    return (
        <Filters
            sections={sections}
            selected={selected}
            onSelectionChange={handleSelectionChange}
            search={value.search}
            onSearchChange={(search) => onChange({ ...value, search })}
            ariaLabel={t('OFFER_REQUESTS.FILTERS.SECTION')}
            searchPlaceholder={t('OFFER_REQUESTS.FILTERS.SEARCH_PLACEHOLDER')}
            dividers
            collapsible
        />
    );
}
